import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthConfig, getUploadConfig } from "./shared";

/*
 * Laboratoire — Ma vitrine (page publique de l'établissement), DEDIE.
 *
 * Port de VitrineModule (HopitalDashboard.js), repointé sur
 * /api/lab/me et /api/lab/establishment/... au lieu de /api/sih/... (voir
 * backend/routes/lab/vitrine.py, fork des mutations ; GET /api/lab/me
 * existait déjà depuis PR3, routes/lab_staff.py). L'original bifurquait sur
 * `ctx.establishment_type === "laboratory"` pour masquer les "chiffres clés"
 * (statistiques hospitalières : patients hospitalisés, interventions...)
 * et n'afficher qu'un seul champ "Horaires d'ouverture" — ici ce cas est
 * toujours vrai, donc directement câblé sans condition.
 */
export default function VitrineModule({ can }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [f, setF] = useState({ description: "", hours_consultations: "", conventions: "" });
  const [coverUrl, setCoverUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [bookletUrl, setBookletUrl] = useState("");
  const [uploading, setUploading] = useState("");
  const canManage = can("staff:write");

  useEffect(() => {
    axios.get(`${API_URL}/api/lab/me`, getAuthConfig()).then((r) => {
      const e = r.data.establishment || {};
      const v = e.vitrine || {};
      setCoverUrl(e.cover_url || "");
      setLogoUrl(e.logo_url || "");
      setBookletUrl(e.booklet_url || "");
      setF({
        description: e.description || "",
        hours_consultations: v.hours_consultations || "",
        conventions: (v.conventions || []).join("\n"),
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setUploading(kind); setError(""); setNotice("");
    const fd = new FormData(); fd.append("file", file);
    const url = kind === "booklet" ? `${API_URL}/api/lab/establishment/booklet` : kind === "logo" ? `${API_URL}/api/lab/establishment/logo` : `${API_URL}/api/lab/establishment/cover`;
    try {
      const r = await axios.post(url, fd, getUploadConfig());
      if (kind === "booklet") { setBookletUrl(r.data.booklet_url); setNotice("Livret mis a jour."); }
      else if (kind === "logo") { setLogoUrl(r.data.logo_url); setNotice("Logo mis a jour."); }
      else { setCoverUrl(r.data.cover_url); setNotice("Banniere mise a jour."); }
    } catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Echec de l'envoi"); }
    finally { setUploading(""); }
  };
  const removeFile = async (kind) => {
    setError(""); setNotice("");
    const url = kind === "booklet" ? `${API_URL}/api/lab/establishment/booklet` : kind === "logo" ? `${API_URL}/api/lab/establishment/logo` : `${API_URL}/api/lab/establishment/cover`;
    try { await axios.delete(url, getAuthConfig()); if (kind === "booklet") setBookletUrl(""); else if (kind === "logo") setLogoUrl(""); else setCoverUrl(""); }
    catch (e) { setError("Erreur"); }
  };

  const save = async () => {
    setSaving(true); setError(""); setNotice("");
    const payload = {
      description: f.description,
      hours_consultations: f.hours_consultations,
      conventions: f.conventions.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try { await axios.put(`${API_URL}/api/lab/establishment/vitrine`, payload, getAuthConfig()); setNotice("Vitrine enregistree. Elle est visible sur la page publique de l'etablissement."); }
    catch (e) { setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
    finally { setSaving(false); }
  };

  const IS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const LB = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };
  const sectionTitle = { fontSize: 12, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5, margin: "22px 0 10px" };

  if (loading) return <div style={{ padding: 30, color: "#94A3B8" }}>Chargement...</div>;

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Ma vitrine</h1>
      <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Ces informations apparaissent sur la page publique de votre etablissement.</div>

      {notice && <div style={{ background: "#EAF3DE", border: "1px solid #CDE3AE", color: "#3B6D11", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{notice}</div>}
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 20, marginTop: 16 }}>
        <div style={{ ...sectionTitle, marginTop: 0 }}>Banniere de l'etablissement</div>
        {coverUrl ? (
          <div>
            <img src={coverUrl} alt="Banniere" style={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 10, border: "1px solid #E2E8F0" }} />
            {canManage && <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>Remplacer<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("cover", e.target.files[0])} /></label>
              <button onClick={() => removeFile("cover")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
            </div>}
          </div>
        ) : (
          canManage && <label style={{ display: "inline-block", fontSize: 13, padding: "9px 16px", border: "1px dashed #CBD5E1", borderRadius: 8, cursor: "pointer", color: "#475569" }}>{uploading === "cover" ? "Envoi..." : "Choisir une image (JPEG/PNG/WEBP, max 5 Mo)"}<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("cover", e.target.files[0])} /></label>
        )}

        <div style={{ ...sectionTitle }}>Logo de l'etablissement</div>
        {logoUrl ? (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src={logoUrl} alt="Logo" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 12, border: "1px solid #E2E8F0", background: "#fff" }} />
            {canManage && <div style={{ display: "flex", gap: 12 }}>
              <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>Remplacer<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("logo", e.target.files[0])} /></label>
              <button onClick={() => removeFile("logo")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
            </div>}
          </div>
        ) : (
          canManage && <label style={{ display: "inline-block", fontSize: 13, padding: "9px 16px", border: "1px dashed #CBD5E1", borderRadius: 8, cursor: "pointer", color: "#475569" }}>{uploading === "logo" ? "Envoi..." : "Choisir un logo (JPEG/PNG/WEBP, carré de préférence)"}<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadFile("logo", e.target.files[0])} /></label>
        )}

        <div style={{ ...sectionTitle, marginTop: 0 }}>Presentation</div>
        <label style={LB}>A propos de l'etablissement</label>
        <textarea style={{ ...IS, minHeight: 90, resize: "vertical" }} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} disabled={!canManage} placeholder="Presentez votre laboratoire, ses analyses, son plateau technique..." />

        <div style={sectionTitle}>Horaires</div>
        <div><label style={LB}>Horaires d'ouverture</label><input style={IS} value={f.hours_consultations} onChange={(e) => setF({ ...f, hours_consultations: e.target.value })} disabled={!canManage} placeholder="Samedi - Jeudi : 08h - 16h" /></div>

        <div style={sectionTitle}>Conventions / mutuelles</div>
        <label style={LB}>Un organisme par ligne</label>
        <textarea style={{ ...IS, minHeight: 90, resize: "vertical" }} value={f.conventions} onChange={(e) => setF({ ...f, conventions: e.target.value })} disabled={!canManage} placeholder={"CNAS\nCASNOS\nMutuelle des travailleurs..."} />

        <div style={sectionTitle}>Livret d'accueil (PDF)</div>
        {bookletUrl ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <a href={bookletUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#185FA5" }}>Voir le livret actuel</a>
            {canManage && <>
              <label style={{ fontSize: 13, color: "#185FA5", cursor: "pointer" }}>Remplacer<input type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => uploadFile("booklet", e.target.files[0])} /></label>
              <button onClick={() => removeFile("booklet")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>
            </>}
          </div>
        ) : (
          canManage && <label style={{ display: "inline-block", fontSize: 13, padding: "9px 16px", border: "1px dashed #CBD5E1", borderRadius: 8, cursor: "pointer", color: "#475569" }}>{uploading === "booklet" ? "Envoi..." : "Choisir un PDF (max 15 Mo)"}<input type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => uploadFile("booklet", e.target.files[0])} /></label>
        )}

        {canManage && <div style={{ marginTop: 18 }}>
          <button onClick={save} disabled={saving} style={{ padding: "9px 20px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer la vitrine"}</button>
        </div>}
      </div>
    </div>
  );
}
